import Portal from "@mui/material/Portal";
import { Network } from "../../fstn/network";
import { createLink } from "../../utility/url";
import AddCommentButton from "../comments/AddCommentButton";
import { CommentType } from "../comments/CommentContainer";
import StateComments from "../comments/StateComments";
import { Projects } from "../../redux/types/firebase";
import EditState, { IGrammarRule } from "./EditState";
import State from "./State";
import { Choices, Grammar, IgnoredGrammar, IScript, StateCommentMap } from "./Writer";

interface Data
{
    states: string[]
    network: Network
    grammar: Grammar,
    mode: string,
    ignoredGrammar: IgnoredGrammar,
    editing?: string,
    project: string,
    projects: Projects,
    stateCommentMap: StateCommentMap,
    commentHighlight?: string,
    choices: Choices,
    stateHighlight?: string,
    canAddComment: boolean,
    handleEdit: (state?: string | undefined) => void,
    handleUpdateText: (index: number, stateKey: string, update: string) => Promise<void>,
    handleChoiceChange: (state: string, choice: string) => void,
    handleUpdateGrammar: (state: string, text: string, update: IGrammarRule[]) => void,
    handleHighlightState: (stateId?: string | undefined) => void,
    handleHighlightComment: (commentId?: string | undefined) => void,
    handleSetHeight: (index: number, height: number) => void,
    updateSelectionRange: (state: string, start: number, end: number) => void,
    getCommentTypeInfo: () => CommentType,
    commentsRefs: React.MutableRefObject<Array<HTMLDivElement>>
}

interface Props
{
    index: number,
    style: React.CSSProperties,
    data: Data
}

export default function ScriptListItem({ index, style, data }: Props)
{
    const { states, network, grammar, mode, ignoredGrammar, editing, project,
        projects, stateCommentMap, commentHighlight, choices, stateHighlight, canAddComment,
        handleEdit, handleUpdateText, handleChoiceChange, handleUpdateGrammar, handleHighlightState,
        handleHighlightComment, handleSetHeight, updateSelectionRange, getCommentTypeInfo, commentsRefs } = data;

    const state = states[index];
    const script: IScript = {
        text: network.states.get(state)?.script || "",
        grammar: (grammar && grammar[state]?.matches) || [],
        ignoredGrammar: (ignoredGrammar && state in ignoredGrammar) ? Object.values(ignoredGrammar[state]) : []
    };

    return (
        <div key={state} style={style}>
            {editing === state ?
                <EditState
                    index={index} key={"Edit" + state} onEdit={handleEdit} stateURL={createLink({ state, project, mode })}
                    project={project} updateScript={handleUpdateText}
                    setSelectionRange={updateSelectionRange}
                    script={script} state={state} onUpdateGrammar={handleUpdateGrammar} /> :
                <State
                    comments={stateCommentMap[state] || []} highlight={stateHighlight === state}
                    commentHighlight={commentHighlight} choices={choices} index={index}
                    key={"View" + state} onChoice={handleChoiceChange} onEdit={handleEdit}
                    onSetHeight={handleSetHeight} onStateHighlight={handleHighlightState}
                    projectDetails={projects[project]} script={script} state={state}
                    updateScript={handleUpdateText} onUpdateGrammar={handleUpdateGrammar}
                    setSelectionRange={updateSelectionRange} />
            }
            {
                (canAddComment && state === stateHighlight) && <AddCommentButton getSelectionDetails={getCommentTypeInfo} />
            }
            <Portal container={commentsRefs.current[index]}>
                <StateComments commentHighlight={commentHighlight}
                    onHighlightComment={handleHighlightComment}
                    state={state} stateHighlight={stateHighlight} />
            </Portal>
        </div>
    );
};